import React from 'react';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HistoryIcon from '@mui/icons-material/History';
import { relativeTime } from './utils';
import type { ReconcilerFailure } from '../../../../types/backend';

export interface FailedResourcesListProps {
  failures: ReconcilerFailure[];
  /** Decode the raw resource_id into human label parts; defaults to a single raw part. */
  formatResourceParts?: (resourceId: string) => string[];
}

const FailureRow: React.FC<{
  failure: ReconcilerFailure;
  formatResourceParts: (resourceId: string) => string[];
}> = ({ failure, formatResourceParts }) => {
  const stale = failure.stale === true;
  const [head, ...rest] = formatResourceParts(failure.resource_id);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        px: 1.5,
        py: 1,
        borderLeft: 3,
        borderColor: stale ? 'divider' : 'error.main',
        bgcolor: stale ? 'transparent' : 'action.hover',
        borderRadius: 0.5,
        opacity: stale ? 0.85 : 1,
      }}
    >
      {stale ? (
        <HistoryIcon sx={{ fontSize: 16, color: 'text.disabled', mt: 0.25, flexShrink: 0 }} />
      ) : (
        <ErrorOutlineIcon sx={{ fontSize: 16, color: 'error.main', mt: 0.25, flexShrink: 0 }} />
      )}

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Tooltip title={failure.resource_id} placement="top-start">
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: stale ? 'text.secondary' : 'text.primary',
                  wordBreak: 'break-word',
                }}
              >
                {head}
              </Typography>
              {stale && (
                <Chip
                  label="stale"
                  size="small"
                  variant="outlined"
                  sx={{
                    height: 18,
                    fontSize: '0.65rem',
                    color: 'text.secondary',
                    borderColor: 'divider',
                    '& .MuiChip-label': { px: 0.75 },
                  }}
                />
              )}
            </Box>
            {rest.map((part, i) => (
              <Typography
                key={i}
                variant="caption"
                sx={{ display: 'block', color: 'text.secondary', lineHeight: 1.35, wordBreak: 'break-word' }}
              >
                {part}
              </Typography>
            ))}
          </Box>
        </Tooltip>

        {failure.error && (
          <Tooltip title={failure.error} placement="top-start">
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                fontFamily: 'monospace',
                color: stale ? 'text.disabled' : 'error.main',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                mt: 0.25,
              }}
            >
              {failure.error}
            </Typography>
          </Tooltip>
        )}
      </Box>

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ flexShrink: 0, whiteSpace: 'nowrap', mt: 0.25 }}
      >
        {relativeTime(failure.failed_at)}
      </Typography>
    </Box>
  );
};

const FailedResourcesList: React.FC<FailedResourcesListProps> = ({
  failures,
  formatResourceParts = (id) => [id],
}) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
    {failures.map((failure, idx) => (
      <FailureRow
        key={`${failure.resource_id}-${failure.failed_at ?? idx}`}
        failure={failure}
        formatResourceParts={formatResourceParts}
      />
    ))}
  </Box>
);

export default FailedResourcesList;
